import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Coffee, Plus, Trash2, Edit, Save, X, ArrowLeft, Sparkles,
  Settings, Eye, Leaf, IceCream, UtensilsCrossed
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const ADMIN_PASSWORD = 'coffee2024';

const CATEGORIES = [
  { id: 'coffee', label: 'Coffee', icon: Coffee },
  { id: 'tea', label: 'Tea', icon: Leaf },
  { id: 'snowcones', label: 'Snow Cones', icon: IceCream },
  { id: 'milkshakes', label: 'Milkshakes', icon: IceCream },
  { id: 'smoothies', label: 'Smoothies', icon: Sparkles },
  { id: 'food', label: 'Food', icon: UtensilsCrossed },
];

const CoffeeOnTheGoAdmin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('menu');
  const [menuItems, setMenuItems] = useState([]);
  const [specials, setSpecials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  
  // New item form
  const [newItem, setNewItem] = useState({
    category: 'coffee',
    name: '',
    description: '',
    price: '',
    hasSizes: false,
    sizes: [{ name: 'Small', price: '' }, { name: 'Medium', price: '' }, { name: 'Large', price: '' }],
    tags: [],
  });

  const [newSpecial, setNewSpecial] = useState({ name: '', price: '', description: '' });

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [menuRes, specialsRes] = await Promise.all([
        fetch(`${API_URL}/api/coffeeonthego/menu`),
        fetch(`${API_URL}/api/coffeeonthego/specials`)
      ]);
      
      const menuData = await menuRes.json();
      const specialsData = await specialsRes.json();
      
      setMenuItems(menuData.items || []);
      setSpecials(specialsData.specials || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      toast.success('Welcome to Coffee On The Go Admin!');
    } else {
      toast.error('Invalid password');
    }
  };

  const handleAddItem = async () => {
    if (!newItem.name) {
      toast.error('Please enter an item name');
      return;
    }

    const itemToAdd = {
      ...newItem,
      sizes: newItem.hasSizes ? newItem.sizes.filter(s => s.price) : undefined,
      price: newItem.hasSizes ? undefined : newItem.price,
    };

    try {
      const res = await fetch(`${API_URL}/api/coffeeonthego/menu`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'x-admin-password': ADMIN_PASSWORD
        },
        body: JSON.stringify(itemToAdd),
      });

      if (res.ok) {
        toast.success(`${newItem.name} added to menu!`);
        resetNewItem();
        setShowAddForm(false);
        fetchData();
      } else {
        toast.error('Failed to add item');
      }
    } catch (error) {
      toast.error('Failed to add item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const res = await fetch(`${API_URL}/api/coffeeonthego/menu/${itemId}`, {
        method: 'DELETE',
        headers: { 'x-admin-password': ADMIN_PASSWORD },
      });

      if (res.ok) {
        toast.success('Item deleted');
        fetchData();
      } else {
        toast.error('Failed to delete item');
      }
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleAddSpecial = async () => {
    if (!newSpecial.name || !newSpecial.price) {
      toast.error('Please enter name and price');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/coffeeonthego/specials`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'x-admin-password': ADMIN_PASSWORD
        },
        body: JSON.stringify(newSpecial),
      });

      if (res.ok) {
        toast.success('Special added!');
        setNewSpecial({ name: '', price: '', description: '' });
        fetchData();
      } else {
        toast.error('Failed to add special');
      }
    } catch (error) {
      toast.error('Failed to add special');
    }
  };

  const handleDeleteSpecial = async (specialId) => {
    try {
      const res = await fetch(`${API_URL}/api/coffeeonthego/specials/${specialId}`, {
        method: 'DELETE',
        headers: { 'x-admin-password': ADMIN_PASSWORD },
      });

      if (res.ok) {
        toast.success('Special removed');
        fetchData();
      } else {
        toast.error('Failed to remove special');
      }
    } catch (error) {
      toast.error('Failed to remove special');
    }
  };

  const resetNewItem = () => {
    setNewItem({
      category: 'coffee',
      name: '',
      description: '',
      price: '',
      hasSizes: false,
      sizes: [{ name: 'Small', price: '' }, { name: 'Medium', price: '' }, { name: 'Large', price: '' }],
      tags: [],
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-900 via-amber-800 to-orange-900 flex items-center justify-center p-4">
        <Helmet>
          <title>Coffee On The Go - Admin</title>
        </Helmet>
        
        <div className="bg-white rounded-3xl p-8 w-full max-w-md shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Coffee className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-black text-amber-900">COFFEE ON THE GO</h1>
            <p className="text-amber-600">Menu Admin Panel</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="text-center"
            />
            <Button type="submit" className="w-full bg-amber-900 hover:bg-amber-800">
              Login
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amber-50">
      <Helmet>
        <title>Coffee On The Go - Admin</title>
      </Helmet>

      {/* Header */}
      <header className="bg-amber-900 text-white py-4 px-6 shadow-lg">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-amber-700 rounded-full flex items-center justify-center">
              <Coffee className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-black">COFFEE ON THE GO</h1>
              <p className="text-amber-300 text-sm">Menu Admin</p>
            </div>
          </div>
          
          <Link to="/coffeeonthego" className="flex items-center gap-2 bg-amber-700 hover:bg-amber-600 px-4 py-2 rounded-lg transition">
            <Eye className="w-4 h-4" />
            View Site
          </Link>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-amber-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('menu')}
              className={`px-6 py-4 font-semibold border-b-2 transition ${
                activeTab === 'menu' ? 'border-amber-900 text-amber-900' : 'border-transparent text-amber-600 hover:text-amber-900'
              }`}
            >
              <Coffee className="w-5 h-5 inline mr-2" />
              Menu Items
            </button>
            <button
              onClick={() => setActiveTab('specials')}
              className={`px-6 py-4 font-semibold border-b-2 transition ${
                activeTab === 'specials' ? 'border-amber-900 text-amber-900' : 'border-transparent text-amber-600 hover:text-amber-900'
              }`}
            >
              <Sparkles className="w-5 h-5 inline mr-2" />
              Today's Specials
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-6xl mx-auto p-6">
        {/* Menu Items Tab */}
        {activeTab === 'menu' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-amber-900">Menu Items</h2>
              <Button 
                onClick={() => setShowAddForm(true)}
                className="bg-amber-900 hover:bg-amber-800"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Item
              </Button>
            </div>

            {/* Menu items by category */}
            {CATEGORIES.map((cat) => {
              const categoryItems = menuItems.filter(item => item.category === cat.id);
              if (categoryItems.length === 0) return null;
              
              const Icon = cat.icon;
              return (
                <div key={cat.id} className="mb-8">
                  <h3 className="text-lg font-bold text-amber-800 mb-4 flex items-center gap-2">
                    <Icon className="w-5 h-5" />
                    {cat.label}
                  </h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {categoryItems.map((item) => (
                      <div key={item.id} className="bg-white rounded-xl p-4 shadow-sm border border-amber-100">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h4 className="font-bold text-amber-900">{item.name}</h4>
                            {item.description && (
                              <p className="text-sm text-amber-600">{item.description}</p>
                            )}
                          </div>
                          <button
                            onClick={() => handleDeleteItem(item.id)}
                            className="text-red-400 hover:text-red-600 p-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        {item.sizes ? (
                          <div className="text-sm text-amber-700">
                            {item.sizes.map((s, idx) => (
                              <span key={idx} className="mr-3">{s.name}: ${s.price}</span>
                            ))}
                          </div>
                        ) : (
                          <div className="text-lg font-bold text-amber-900">${item.price}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {menuItems.length === 0 && !loading && (
              <div className="text-center py-12 text-amber-600">
                <Coffee className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>No menu items yet. Add your first item!</p>
              </div>
            )}
          </div>
        )}

        {/* Specials Tab */}
        {activeTab === 'specials' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-amber-900">Today's Specials</h2>
            </div>

            {/* Add Special Form */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-amber-100 mb-6">
              <h3 className="font-bold text-amber-900 mb-4">Add a Special</h3>
              <div className="grid md:grid-cols-4 gap-4">
                <Input
                  placeholder="Special name"
                  value={newSpecial.name}
                  onChange={(e) => setNewSpecial({...newSpecial, name: e.target.value})}
                />
                <Input
                  placeholder="Price"
                  value={newSpecial.price}
                  onChange={(e) => setNewSpecial({...newSpecial, price: e.target.value})}
                />
                <Input
                  placeholder="Description (optional)"
                  value={newSpecial.description}
                  onChange={(e) => setNewSpecial({...newSpecial, description: e.target.value})}
                />
                <Button onClick={handleAddSpecial} className="bg-amber-900 hover:bg-amber-800">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Special
                </Button>
              </div>
            </div>

            {/* Current Specials */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {specials.map((special) => (
                <div key={special.id} className="bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-4 text-white">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-bold text-lg">{special.name}</h4>
                      {special.description && <p className="text-white/80 text-sm">{special.description}</p>}
                      <div className="text-2xl font-black mt-2">${special.price}</div>
                    </div>
                    <button
                      onClick={() => handleDeleteSpecial(special.id)}
                      className="text-white/70 hover:text-white p-1"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {specials.length === 0 && (
              <div className="text-center py-12 text-amber-600">
                <Sparkles className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>No specials today. Add one to show it on the website!</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Add Item Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-amber-900">Add Menu Item</h2>
              <button onClick={() => { setShowAddForm(false); resetNewItem(); }} className="text-amber-400 hover:text-amber-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-amber-700 mb-1">Category</label>
                <select
                  value={newItem.category}
                  onChange={(e) => setNewItem({...newItem, category: e.target.value})}
                  className="w-full px-4 py-2 border border-amber-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-amber-700 mb-1">Item Name</label>
                <Input
                  placeholder="e.g., Flat White"
                  value={newItem.name}
                  onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-amber-700 mb-1">Description (optional)</label>
                <Input
                  placeholder="e.g., Smooth & silky"
                  value={newItem.description}
                  onChange={(e) => setNewItem({...newItem, description: e.target.value})}
                />
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newItem.hasSizes}
                    onChange={(e) => setNewItem({...newItem, hasSizes: e.target.checked})}
                    className="rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                  />
                  <span className="text-sm font-medium text-amber-700">Has multiple sizes</span>
                </label>
              </div>

              {newItem.hasSizes ? (
                <div>
                  <label className="block text-sm font-medium text-amber-700 mb-2">Sizes & Prices</label>
                  {newItem.sizes.map((size, idx) => (
                    <div key={idx} className="flex gap-2 mb-2">
                      <Input
                        placeholder="Size name"
                        value={size.name}
                        onChange={(e) => {
                          const newSizes = [...newItem.sizes];
                          newSizes[idx].name = e.target.value;
                          setNewItem({...newItem, sizes: newSizes});
                        }}
                        className="flex-1"
                      />
                      <Input
                        placeholder="Price"
                        value={size.price}
                        onChange={(e) => {
                          const newSizes = [...newItem.sizes];
                          newSizes[idx].price = e.target.value;
                          setNewItem({...newItem, sizes: newSizes});
                        }}
                        className="w-24"
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setNewItem({...newItem, sizes: [...newItem.sizes, { name: '', price: '' }]})}
                    className="text-sm text-amber-600 hover:text-amber-800"
                  >
                    + Add another size
                  </button>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-amber-700 mb-1">Price</label>
                  <Input
                    placeholder="e.g., 5.50"
                    value={newItem.price}
                    onChange={(e) => setNewItem({...newItem, price: e.target.value})}
                  />
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => { setShowAddForm(false); resetNewItem(); }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddItem}
                  className="flex-1 bg-amber-900 hover:bg-amber-800"
                >
                  Add to Menu
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoffeeOnTheGoAdmin;
